import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Globe2, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Loader2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { region: "", country: "", context: "", note: "", flag: "", educators: 0, students: 0, assets: [] };

export function ImpactRegionsAdmin() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // region object or EMPTY (create)
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/impact/regions")
      .then(({ data }) => setRegions(data.regions))
      .catch(() => toast.error("Could not load regions."))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!editing.region || !editing.country) return toast.error("Region and country are required.");
    setSaving(true);
    const payload = {
      ...editing,
      educators: Number(editing.educators) || 0,
      students: Number(editing.students) || 0,
      assets: (Array.isArray(editing.assets) ? editing.assets.join(", ") : editing.assets || "")
        .split(",").map((a) => a.trim()).filter(Boolean),
    };
    try {
      if (editing.id) await api.put(`/admin/impact/regions/${editing.id}`, payload);
      else await api.post("/admin/impact/regions", payload);
      toast.success(editing.id ? "Region updated." : "Region added.");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/admin/impact/regions/${r.id}`);
      toast.success(`Removed ${r.country}.`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const move = async (idx, dir) => {
    const next = [...regions];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setRegions(next); // optimistic
    try {
      await api.put("/admin/impact/regions/reorder", { ids: next.map((r) => r.id) });
    } catch (err) {
      toast.error("Could not reorder.");
      await load();
    }
  };

  const field = (k) => (e) => setEditing((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 mt-8" data-testid="impact-regions-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe2 className="h-5 w-5 text-persian-blue" />
          <h2 className="font-serif text-xl text-navy">Global Impact Regions</h2>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="region-add-button"
          className="bg-navy hover:bg-navy-light text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add region
        </Button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-persian-blue" /></div>
      ) : regions.length === 0 ? (
        <p className="py-8 text-center text-slate-500 text-sm" data-testid="regions-empty">No regions yet. Add the first one.</p>
      ) : (
        <ul className="space-y-2" data-testid="admin-regions-list">
          {regions.map((r, i) => (
            <li key={r.id} data-testid={`admin-region-${r.id}`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`region-up-${r.id}`}
                  className="text-slate-400 hover:text-navy disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === regions.length - 1} data-testid={`region-down-${r.id}`}
                  className="text-slate-400 hover:text-navy disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
              </div>
              <span className="text-xl leading-none w-6 text-center">{r.flag || "🌐"}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-navy truncate">{r.country} · <span className="text-slate-500">{r.region}</span></div>
                <div className="text-xs text-slate-500 truncate">{r.note}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setEditing({ ...r })} data-testid={`region-edit-${r.id}`}
                className="text-slate-400 hover:text-navy h-8 w-8"><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`region-delete-${r.id}`}
                className="text-slate-400 hover:text-persian-rose h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="region-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{editing?.id ? "Edit region" : "Add region"}</DialogTitle>
            <DialogDescription className="text-slate-500">These profiles appear on the public Global Impact page.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-3 pt-1 max-h-[65vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Input value={editing.country} onChange={field("country")} data-testid="region-field-country" placeholder="e.g. Kenya" />
                </div>
                <div className="space-y-1.5">
                  <Label>Region / City</Label>
                  <Input value={editing.region} onChange={field("region")} data-testid="region-field-region" placeholder="e.g. Nairobi" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Flag emoji</Label>
                  <Input value={editing.flag} onChange={field("flag")} data-testid="region-field-flag" placeholder="🇰🇪" />
                </div>
                <div className="space-y-1.5">
                  <Label>Context</Label>
                  <Input value={editing.context} onChange={field("context")} data-testid="region-field-context" placeholder="Community learning circles" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Note</Label>
                <Textarea value={editing.note} onChange={field("note")} rows={2} data-testid="region-field-note"
                  placeholder="One-line description of the work in this region." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Educators</Label>
                  <Input type="number" min="0" value={editing.educators} onChange={field("educators")} data-testid="region-field-educators" />
                </div>
                <div className="space-y-1.5">
                  <Label>Students</Label>
                  <Input type="number" min="0" value={editing.students} onChange={field("students")} data-testid="region-field-students" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Assets (comma-separated)</Label>
                <Input value={Array.isArray(editing.assets) ? editing.assets.join(", ") : editing.assets}
                  onChange={field("assets")} data-testid="region-field-assets"
                  placeholder="Dedicated educators, Premium notebooks & pens" />
              </div>
              <Button type="submit" disabled={saving} data-testid="region-save-button"
                className="w-full bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {editing.id ? "Save changes" : "Add region"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
